import type { TriageCandidate, TriageRecent } from "@/lib/api";
import { DOMAIN_LABEL, dayLabel, relativeAge } from "@/lib/overview";
import {
  ACTION_LABEL,
  BUCKET_LABEL,
  type Staged,
  addedText,
  bucketOf,
  labelOf,
} from "@/lib/triage";
import { Box, Text } from "@okshaun/components";
import { css } from "@styled-system/css";
import { useState } from "react";
import { EvidenceList } from "./evidence-list";

const DOMAIN_INK = {
  cetec: "domain.cetec",
  mkbd: "domain.mkbd",
  personal: "domain.personal",
} as const;

const LABEL_INK: Record<string, string> = {
  "likely done": "text.success",
  stale: "text.warning",
  unlabeled: "text.subtlest",
  live: "text.accent.blue",
  waiting: "text.accent.purple",
  snoozed: "text.subtle",
};

const disclosure = css({
  textStyle: "mono.xs",
  color: "text.subtlest",
  bg: "transparent",
  border: "none",
  p: "0",
  cursor: "pointer",
  textAlign: "left",
  outline: "none",
  _hover: { color: "text" },
  _focusVisible: { color: "text", textDecoration: "underline" },
});

interface TriageCardProps {
  card: TriageCandidate;
  today: string;
  labelsAt: string | null;
  staged?: Staged;
  recent?: TriageRecent;
}

/** One task, with what Hal found about it. Flat: hairline-framed, no nested panels. */
export function TriageCard({ card, today, labelsAt, staged, recent }: TriageCardProps) {
  const [showRaw, setShowRaw] = useState(false);
  const bucket = bucketOf(card, today);
  const label = BUCKET_LABEL[bucket];
  // A label the page refuses to show (Mockingbird "stale") shows nothing of Hal's judgment.
  const judged = labelOf(card) ? card.label : null;
  const added = addedText(card, dayLabel);
  const notTasks = !card.inTasks && card.section ? card.section.replace(/^#+\s*/, "") : null;

  return (
    <Box as="article" display="flex" flexDir="column" gap="16" aria-label={`Task: ${card.text}`}>
      <Box display="flex" gap="8" alignItems="baseline" flexWrap="wrap">
        <Text textStyle="mono.xs" color={DOMAIN_INK[card.domain]}>
          {DOMAIN_LABEL[card.domain]}
        </Text>
        <Text fontSize="13" fontWeight="semibold" color="text.bold">
          {card.projectName}
        </Text>
        {card.noteStatus !== "active" && (
          <Text textStyle="mono.xs" color="text.warning">
            note is {card.noteStatus}
          </Text>
        )}
        {notTasks && (
          <Text textStyle="mono.xs" color="text.subtlest">
            under “{notTasks}”, not ## Tasks
          </Text>
        )}
        <Text textStyle="mono.xs" color="text.subtlest" ml="auto">
          line {card.line}
        </Text>
      </Box>

      {card.parent && (
        <Text textStyle="body.sm" color="text.subtle">
          Part of: {card.parent.text}
        </Text>
      )}

      <Text as="p" fontSize="[18px]" lineHeight="[1.45]" color="text.bold" m="0">
        {card.text}
      </Text>

      <Box display="flex" gap="16" flexWrap="wrap">
        <Text
          textStyle="mono.xs"
          color="text.subtlest"
          title={added.title}
          cursor="help"
          aria-label={`${added.text}. ${added.title}`}
        >
          {added.text}
        </Text>
        {card.due && (
          <Text textStyle="mono.xs" color={card.due < today ? "text.danger" : "text.warning"}>
            deadline {dayLabel(card.due)}
            {card.due < today ? " (past)" : ""}
          </Text>
        )}
        {card.remindOn && (
          <Text textStyle="mono.xs" color="text.subtle">
            reminder {dayLabel(card.remindOn)}
          </Text>
        )}
        {card.waiting && (
          <Text textStyle="mono.xs" color="text.accent.purple">
            waiting on someone else
          </Text>
        )}
        {card.children.length > 0 && (
          <Text textStyle="mono.xs" color="text.subtle">
            {card.children.length} open sub-task{card.children.length === 1 ? "" : "s"}
          </Text>
        )}
      </Box>

      {card.children.length > 0 && (
        <Box as="ul" display="flex" flexDir="column" gap="2" pl="16" m="0">
          {card.children.slice(0, 8).map((c) => (
            <Text as="li" key={c.id} fontSize="13" color="text.subtle">
              {c.text}
            </Text>
          ))}
          {card.children.length > 8 && (
            <Text as="li" textStyle="mono.xs" color="text.subtlest" listStyle="none">
              and {card.children.length - 8} more
            </Text>
          )}
        </Box>
      )}

      <Box display="flex" flexDir="column" gap="8" borderTop="default" pt="12">
        <Box display="flex" gap="8" alignItems="baseline" flexWrap="wrap">
          <Text
            textStyle="mono.xs"
            color={LABEL_INK[label]}
            textTransform="uppercase"
            letterSpacing="widest"
          >
            {label}
          </Text>
          {judged && (
            <Text textStyle="mono.xs" color="text.subtlest">
              {judged.confidence}
              {judged.suggest ? ` · suggests ${judged.suggest}` : ""}
            </Text>
          )}
          <Text textStyle="mono.xs" color="text.subtlest" ml="auto">
            {labelsAt ? `labeled ${relativeAge(labelsAt)} · work` : "no labels yet"}
          </Text>
        </Box>
        {judged ? (
          <>
            <Text fontSize="13" color="text">
              {judged.why}
            </Text>
            <EvidenceList items={judged.evidence} />
          </>
        ) : (
          <Text fontSize="13" color="text.subtle">
            Hal has not judged this task. Decide from the text, or add a note.
          </Text>
        )}
      </Box>

      {recent?.outcome === "skipped" && (
        <Text textStyle="mono.xs" color="text.warning">
          Last time Hal skipped this ({recent.why ?? "no reason"}). Decide again.
        </Text>
      )}

      {staged && (
        <Text textStyle="mono.xs" color="text.selected">
          Staged: {ACTION_LABEL[staged.action]}
          {staged.date ? ` → ${dayLabel(staged.date)}` : ""}
          {staged.includeChildren ? " (with sub-tasks)" : ""}
          {staged.note ? " · with a note" : ""}
        </Text>
      )}

      <Box display="flex" flexDir="column" gap="6">
        <button
          type="button"
          className={disclosure}
          onClick={() => setShowRaw((v) => !v)}
          aria-expanded={showRaw}
        >
          {showRaw ? "Hide the line" : "Show the line"}
        </button>
        {showRaw && (
          <Text
            as="pre"
            textStyle="mono.xs"
            color="text.subtle"
            whiteSpace="pre-wrap"
            wordBreak="break-word"
            m="0"
            p="8"
            bg="surface.sunken"
            borderRadius="4"
          >
            {card.path}
            {"\n"}
            {card.raw}
          </Text>
        )}
      </Box>
    </Box>
  );
}
